import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { lspAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Search, Filter, MapPin, DollarSign, Calendar, TrendingUp,
  Building2, ArrowRight, CheckCircle, Clock, RefreshCw,
  AlertTriangle, Shield, Eye, Briefcase
} from 'lucide-react';

const LSPMarketplace = () => {
  const [deals, setDeals] = useState([]);
  const [filteredDeals, setFilteredDeals] = useState([]);
  const [myCommitments, setMyCommitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [verificationError, setVerificationError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterDeals();
  }, [deals, searchTerm, stateFilter]);

  const fetchData = async () => {
    try {
      const [dealsRes, commitmentsRes] = await Promise.all([
        lspAPI.getMarketplace().catch(err => {
          if (err.response?.status === 403) {
            setVerificationError(err.response.data.detail);
          }
          return { data: [] };
        }),
        lspAPI.getCommitments().catch(() => ({ data: [] }))
      ]);
      
      setDeals(dealsRes.data);
      setMyCommitments(commitmentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterDeals = () => {
    let filtered = [...deals];
    
    if (searchTerm) {
      filtered = filtered.filter(d => 
        d.deal_nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.property_city.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (stateFilter !== 'all') {
      filtered = filtered.filter(d => d.property_state === stateFilter);
    }
    
    setFilteredDeals(filtered);
  };

  const handleCommit = async (dealId) => {
    setCommitting(true);
    try {
      await lspAPI.createCommitment(dealId);
      toast.success('Commitment submitted successfully! Admin will review your request.');
      fetchData();
      setSelectedDeal(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to commit to deal');
    } finally {
      setCommitting(false);
    }
  };

  const isCommitted = (dealId) => {
    return myCommitments.some(c => c.deal_id === dealId);
  };

  const getUniqueStates = () => {
    const states = [...new Set(deals.map(d => d.property_state))];
    return states.sort();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <Layout title="Deal Marketplace">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  // Show verification required message
  if (verificationError) {
    return (
      <Layout title="Deal Marketplace">
        <Card className="glass-effect border-amber-200 dark:border-amber-800">
          <CardContent className="py-12">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
                Verification Required
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-md mx-auto">
                {verificationError}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-500 mb-6">
                Complete your verification to access the deal marketplace and start supporting borrowers.
              </p>
              <Button 
                className="gradient-lsp border-0"
                onClick={() => window.location.href = '/lsp/verification'}
              >
                <Shield className="w-4 h-4 mr-2" />
                Complete Verification
              </Button>
            </div>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout title="Deal Marketplace">
      <div className="space-y-6 animate-slide-in-up" data-testid="lsp-marketplace">
        {/* Info Banner */}
        <Card className="glass-effect border-purple-200 dark:border-purple-800 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 gradient-lsp rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-100">Anonymized Deal Listings</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  All deals shown are anonymized to protect borrower privacy. Borrower details will only be shared after admin approval of your commitment.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="glass-effect border-slate-200 dark:border-slate-700">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by deal name or city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="marketplace-search"
                />
              </div>
              <div className="w-48">
                <Select value={stateFilter} onValueChange={setStateFilter}>
                  <SelectTrigger data-testid="state-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by state" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All States</SelectItem>
                    {getUniqueStates().map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={fetchData} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Available Deals</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{deals.length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">My Commitments</p>
              <p className="text-2xl font-bold text-purple-600">{myCommitments.length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Total Liquidity Needed</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(deals.reduce((sum, d) => sum + (d.liquidity_amount_provided || 0), 0))}
              </p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Avg Reserve Required</p>
              <p className="text-2xl font-bold text-blue-600">
                {deals.length > 0 ? formatCurrency(deals.reduce((sum, d) => sum + (d.reserve_required_amount || 0), 0) / deals.length) : '$0'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Deals Grid */}
        {filteredDeals.length === 0 ? (
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                No Deals Available
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                {deals.length === 0 
                  ? "There are currently no active deals in the marketplace. Check back soon!"
                  : "No deals match your search criteria. Try adjusting your filters."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDeals.map((deal) => (
              <Card 
                key={deal.id}
                className={`glass-effect border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all hover-lift ${
                  isCommitted(deal.id) ? 'ring-2 ring-purple-500 ring-opacity-50' : ''
                }`}
                data-testid={`deal-card-${deal.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
                        {deal.deal_nickname}
                      </CardTitle>
                      <div className="flex items-center gap-1 mt-1 text-sm text-slate-500 dark:text-slate-400">
                        <MapPin className="w-4 h-4" />
                        {deal.property_city}, {deal.property_state}
                      </div>
                    </div>
                    {isCommitted(deal.id) && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full text-xs font-medium">
                        <CheckCircle className="w-3 h-3" />
                        Committed
                      </span>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Financial Summary */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Purchase Price</p>
                      <p className="font-semibold text-slate-900 dark:text-slate-100">
                        {formatCurrency(deal.purchase_price)}
                      </p>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 dark:text-slate-400">ARV</p>
                      <p className="font-semibold text-slate-900 dark:text-slate-100">
                        {formatCurrency(deal.arv)}
                      </p>
                    </div>
                  </div>

                  {/* Key Metrics */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400 flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        Liquidity Needed
                      </span>
                      <span className="font-semibold text-green-600 dark:text-green-400">
                        {formatCurrency(deal.liquidity_amount_provided)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400 flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        Reserve Required
                      </span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        {formatCurrency(deal.reserve_required_amount)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400 flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Est. Closing
                      </span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        {new Date(deal.est_closing_date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400 flex items-center gap-1">
                        <TrendingUp className="w-4 h-4" />
                        Exit Strategy
                      </span>
                      <span className="font-medium text-slate-900 dark:text-slate-100 capitalize">
                        {deal.exit_strategy}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                    {isCommitted(deal.id) ? (
                      <Button 
                        variant="outline" 
                        className="w-full border-purple-300 text-purple-600 dark:border-purple-700 dark:text-purple-400"
                        disabled
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Commitment Pending Review
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button 
                          variant="outline"
                          className="flex-1"
                          onClick={() => setSelectedDeal(selectedDeal?.id === deal.id ? null : deal)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {selectedDeal?.id === deal.id ? 'Hide' : 'Details'}
                        </Button>
                        <Button 
                          className="flex-1 gradient-lsp border-0"
                          onClick={() => handleCommit(deal.id)}
                          disabled={committing}
                          data-testid={`commit-btn-${deal.id}`}
                        >
                          <ArrowRight className="w-4 h-4 mr-1" />
                          Commit
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Expanded Details */}
                  {selectedDeal?.id === deal.id && (
                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 animate-slide-in-up">
                      <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Deal Details</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Rehab Budget</span>
                          <span className="text-slate-900 dark:text-slate-100">{formatCurrency(deal.rehab_budget)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Total Investment</span>
                          <span className="text-slate-900 dark:text-slate-100">{formatCurrency(deal.purchase_price + deal.rehab_budget)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Potential Profit</span>
                          <span className="text-green-600 font-medium">{formatCurrency(deal.arv - deal.purchase_price - deal.rehab_budget)}</span>
                        </div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                        <p className="text-sm text-purple-700 dark:text-purple-400">
                          <strong>Note:</strong> By committing, you agree to provide the liquidity amount shown. 
                          Your commitment will be reviewed by an admin before being finalized.
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LSPMarketplace;
